import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Auth gate for routes that require a signed-in user (e.g. the Library).
 *
 * - While the auth state is still loading, render a small spinner instead of
 *   leaking children or bouncing to /auth.
 * - When unauthenticated, redirect to /auth?redirect=<original-path>. AuthPage
 *   already honors `?redirect=` and will return the user here on success.
 */
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    const target = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(target)}`} replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
